import {
  ArrowRight,
  Boxes,
  CalendarClock,
  Layers,
  UserRound,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { StatusBadge, type StatusTone } from "@/components/status-badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { DashboardActiveUsinage } from "@/features/dashboard/api/types"
import { formatDate, formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

const usinageStatusTone: Record<DashboardActiveUsinage["status"], StatusTone> =
  {
    SCHEDULED: "muted",
    RUNNING: "info",
    PENDING_COUPLING: "warning",
  }

interface UsinageRowProps {
  usinage: DashboardActiveUsinage
}

function UsinageRow({ usinage }: UsinageRowProps) {
  const { t } = useTranslation("dashboard")
  const inputMt = formatNumber(kgToMt(usinage.inputKg), 1)
  const dateLabel = usinage.startDate
    ? usinage.status === "SCHEDULED"
      ? t("activeUsinages.scheduledFor", {
          date: formatDate(usinage.startDate),
        })
      : t("activeUsinages.startedOn", { date: formatDate(usinage.startDate) })
    : null

  return (
    <Link
      to={paths.usinageDetail(usinage.id)}
      className="hover:bg-muted/40 group -mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold">
            {t("activeUsinages.usinageLabel", {
              number: usinage.usinageNumber,
            })}
          </p>
          <StatusBadge tone={usinageStatusTone[usinage.status]}>
            {t(`activeUsinages.status.${usinage.status}`)}
          </StatusBadge>
        </div>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
          {dateLabel ? (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <CalendarClock className="size-3.5" />
              {dateLabel}
            </span>
          ) : null}
          {usinage.operator ? (
            <span className="inline-flex items-center gap-1 truncate">
              <UserRound className="size-3.5" />
              {usinage.operator}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Boxes className="size-3.5" />
            {t("activeUsinages.loadsAssigned", {
              count: usinage.loadsAssigned,
            })}
          </span>
        </div>
      </div>
      <div className="text-right">
        <p className="text-base font-semibold tabular-nums leading-tight">
          {inputMt} <span className="text-muted-foreground text-xs">MT</span>
        </p>
        <p className="text-muted-foreground text-xs tabular-nums">
          {t("activeUsinages.bagsCount", { count: usinage.inputBags })}
        </p>
      </div>
      <ArrowRight className="text-muted-foreground/50 group-hover:text-muted-foreground size-4 shrink-0 transition-colors" />
    </Link>
  )
}

interface ActiveUsinagesProps {
  usinages: DashboardActiveUsinage[]
}

export function ActiveUsinages({ usinages }: ActiveUsinagesProps) {
  const { t } = useTranslation("dashboard")

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{t("activeUsinages.title")}</CardTitle>
        <Link
          to={paths.millFloor}
          className="text-primary inline-flex items-center gap-1 text-xs font-medium hover:underline"
        >
          {t("activeUsinages.viewMillFloor")}
          <ArrowRight className="size-3.5" />
        </Link>
      </CardHeader>
      <CardContent>
        {usinages.length === 0 ? (
          <EmptyState
            icon={<Layers className="size-6" />}
            title={t("activeUsinages.empty.title")}
            description={t("activeUsinages.empty.description")}
            className="py-6"
          />
        ) : (
          <ul className="divide-border flex flex-col divide-y">
            {usinages.map((u) => (
              <li key={u.id}>
                <UsinageRow usinage={u} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
